import { debounce } from "lodash";
import { useRef, useState, useLayoutEffect } from "react";
import ResizeObserver from "resize-observer-polyfill";
const observers = {};
const getObserver = (prevSizeRef, setSize, currentRef, uniqueKey) => {
    const debounceDelay = 0;
    if (!observers[uniqueKey]) {
        const newObserver = new ResizeObserver(debounce(([entry]) => {
            const newWidth = entry.contentRect.width;
            const newHeight = entry.contentRect.height;
            if (newWidth !== 0 && newHeight !== 0 &&
                (newWidth !== prevSizeRef.current.width || newHeight !== prevSizeRef.current.height)) {
                const newSize = { height: newHeight, width: newWidth };
                prevSizeRef.current = newSize;
                setSize(newSize);
            }
        }, debounceDelay));
        if (currentRef) {
            newObserver.observe(currentRef);
        }
        observers[uniqueKey] = newObserver;
    }
    return () => {
        if (currentRef) {
            observers[uniqueKey]?.unobserve(currentRef);
            delete observers[uniqueKey];
        }
    };
};
/**
 * This hooks will return height and width of the first found object with the supplied classname.
 * It will not register change when going to 0 size (hidden) or returning from 0 (showing)
 * @param className classname of the watched div
 */
const initialSize = { height: 0, width: 0 };
export const useSmartResizeObserverByClassName = (className) => {
    const [size, setSize] = useState(initialSize);
    const prevSizeRef = useRef(initialSize);
    useLayoutEffect(() => {
        const [currentRef] = document.getElementsByClassName(className);
        return getObserver(prevSizeRef, setSize, currentRef, className);
    }, [className]);
    return size;
};
/**
 * This hooks will return height and width of the passed ref.
 * It will not register change when going to 0 size (hidden) or returning from 0 (showing)
 * @param ref ref to the watched div
 * @param uniqueKey key used to store observer in Record
 */
export const useSmartResizeObserver = (ref, uniqueKey) => {
    const [size, setSize] = useState(initialSize);
    const prevSizeRef = useRef(initialSize);
    useLayoutEffect(() => {
        const currentRef = ref.current;
        return getObserver(prevSizeRef, setSize, currentRef, uniqueKey);
    }, [ref, uniqueKey]);
    return size;
};
